import { useEffect, useState } from "react";
import {
  Sheet,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from "@breakingthelines/design-system";

import {
  grantMembership,
  listPlatformMembershipTiers,
  type MembershipDTO,
  type MembershipTierDTO,
} from "@/lib/admin-api";
import {
  canSubmitGrant,
  DURATION_PRESET_MONTHS,
  formatConsequence,
  MIN_REASON_LENGTH,
  resolveDurationToInstant,
  selectCanonicalTierOptions,
  type DurationChoice,
} from "@/lib/scholarship";

import styles from "./grant-scholarship-sheet.module.css";

export interface GrantScholarshipSheetProps {
  open: boolean;
  onClose: () => void;
  subscriber: { id: string; username?: string; displayName?: string };
  onGranted?: (membership: MembershipDTO) => void;
}

function subscriberLabel(subscriber: GrantScholarshipSheetProps["subscriber"]): string {
  return (
    subscriber.displayName || (subscriber.username ? `@${subscriber.username}` : subscriber.id)
  );
}

function presetLabel(months: number): string {
  return months === 1 ? "1 month" : `${months} months`;
}

export function GrantScholarshipSheet({
  open,
  onClose,
  subscriber,
  onGranted,
}: GrantScholarshipSheetProps) {
  const toast = useToast();

  const [tiers, setTiers] = useState<MembershipTierDTO[]>([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [tiersError, setTiersError] = useState<string | null>(null);

  const [tierId, setTierId] = useState<string | null>(null);
  const [choice, setChoice] = useState<DurationChoice | null>(null);
  const [customDateInput, setCustomDateInput] = useState("");
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTiersLoading(true);
    setTiersError(null);

    listPlatformMembershipTiers()
      .then((data) => {
        if (cancelled) return;
        setTiers((data.tiers ?? []).filter((tier) => tier.isActive));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setTiersError(err.message ?? "Failed to load tiers");
        setTiers([]);
      })
      .finally(() => {
        if (!cancelled) setTiersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  function resetForm() {
    setTierId(null);
    setChoice(null);
    setCustomDateInput("");
    setReason("");
    setReasonTouched(false);
    setSubmitError(null);
  }

  function handleClose() {
    onClose();
    resetForm();
  }

  // Collapse the full tier list (a separate row per currency x cadence — see
  // selectCanonicalTierOptions's doc comment) down to exactly the two things
  // a scholarship can grant: Pro and Line Breakers. No Free, no cadence or
  // currency choice — none of that is meaningful for a comp.
  const tierOptions = selectCanonicalTierOptions(tiers);
  // `items` is the Base UI-intended mechanism for a Select trigger to render
  // a label without walking SelectItem children (the DS's Select is a bare
  // `Select.Root` re-export with no `.d.ts` prop checking on this component —
  // see grant-scholarship-sheet.test.tsx for the regression coverage). The
  // null entry is metadata only — it is never rendered as a SelectItem — and
  // exists so <SelectValue> resolves the "nothing selected" state to real
  // placeholder text instead of an empty string.
  const selectItems = [
    { value: null, label: "Select a tier" },
    ...tierOptions.map((option) => ({ value: option.id, label: option.label })),
  ];
  const selectedOption = tierOptions.find((option) => option.id === tierId);
  const resolvedExpiresAt = choice ? resolveDurationToInstant(choice) : null;
  const showConsequence = choice !== null && selectedOption != null;
  const reasonInvalid = reasonTouched && reason.trim().length < MIN_REASON_LENGTH;
  const canSubmit = canSubmitGrant({ tierId, reason, choice }) && !submitting;

  async function handleSubmit() {
    if (!canSubmitGrant({ tierId, reason, choice }) || choice === null || !tierId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const expiresAt = resolveDurationToInstant(choice);
      const { membership } = await grantMembership({
        subscriberId: subscriber.id,
        tierId,
        reason: reason.trim(),
        expiresAt: expiresAt ? expiresAt.toISOString() : undefined,
      });
      toast.success(
        `${subscriberLabel(subscriber)} now has ${selectedOption?.label ?? "the granted tier"}.`,
        "Granted",
      );
      onGranted?.(membership);
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to grant membership");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={handleClose}
      title="Grant scholarship"
      widthClass="w-full sm:w-[440px]"
    >
      <div className={styles.form}>
        <p className={styles.subtitle}>
          Grant a BTL platform membership to <strong>{subscriberLabel(subscriber)}</strong>.
        </p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Tier</span>
          {tiersLoading ? (
            <span className={styles.fieldHint}>Loading tiers…</span>
          ) : tiersError ? (
            <span className={styles.errorText}>{tiersError}</span>
          ) : tierOptions.length === 0 ? (
            <span className={styles.errorText}>
              No Pro or Line Breakers tier is configured — nothing to grant.
            </span>
          ) : (
            <Select items={selectItems} value={tierId} onValueChange={setTierId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tierOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </label>

        <div className={styles.fieldBlock}>
          <span className={styles.fieldLabel}>Duration</span>
          <p className={styles.fieldHint}>
            Nothing is preselected. Choose how long this grant lasts — or that it never ends.
          </p>
          <div className={styles.durationRow}>
            {DURATION_PRESET_MONTHS.map((months) => (
              <button
                key={months}
                type="button"
                className={`${styles.durationChip} ${
                  choice?.kind === "preset" && choice.months === months
                    ? styles.durationChipActive
                    : ""
                }`}
                onClick={() => setChoice({ kind: "preset", months })}
              >
                {presetLabel(months)}
              </button>
            ))}
            <button
              type="button"
              className={`${styles.durationChip} ${choice?.kind === "custom" ? styles.durationChipActive : ""}`}
              onClick={() => {
                setChoice({
                  kind: "custom",
                  date: customDateInput ? new Date(customDateInput) : new Date(Number.NaN),
                });
              }}
            >
              Custom date
            </button>
            <button
              type="button"
              className={`${styles.durationChip} ${styles.durationChipPermanent} ${
                choice?.kind === "permanent" ? styles.durationChipActive : ""
              }`}
              onClick={() => setChoice({ kind: "permanent" })}
            >
              Permanent
            </button>
          </div>

          {choice?.kind === "custom" ? (
            <input
              type="date"
              className={styles.dateInput}
              value={customDateInput}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCustomDateInput(value);
                setChoice({
                  kind: "custom",
                  date: value ? new Date(`${value}T00:00:00.000Z`) : new Date(Number.NaN),
                });
              }}
            />
          ) : null}
        </div>

        {showConsequence ? (
          <div
            className={`${styles.consequence} ${resolvedExpiresAt === null ? styles.consequencePermanent : ""}`}
          >
            {formatConsequence({ expiresAt: resolvedExpiresAt })}
          </div>
        ) : null}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Reason</span>
          <Textarea
            placeholder="Why is this being granted? At least 3 characters."
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            onBlur={() => setReasonTouched(true)}
            rows={3}
          />
          {reasonInvalid ? (
            <span className={styles.errorText}>Reason must be at least 3 characters.</span>
          ) : null}
        </label>

        {submitError ? (
          <p className={styles.errorText} role="alert">
            {submitError}
          </p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.grantButton}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {submitting ? "Granting…" : "Grant"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
